import { useTabLeadership } from "$global-providers/tab-leadership/provider";
import { useLazyUser } from "$global-providers/user-provider";
import { isDevEnvironment } from "$routes/_lib/environment";
import { gql } from "@apollo/client";
import { captureException } from "@sentry/react";
import { DeviceSecretPurpose, LookupKeyOrigin } from "elephant-web/gql";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  addRxPlugin,
  createRxDatabase,
  type RxCollection,
  type RxCollectionCreator,
  type RxDatabase,
  RxError,
  type RxStorage,
  removeRxDatabase,
} from "rxdb";
import { RxDBCleanupPlugin } from "rxdb/plugins/cleanup";
import { wrappedKeyEncryptionCryptoJsStorage } from "rxdb/plugins/encryption-crypto-js";
import { RxDBLeaderElectionPlugin } from "rxdb/plugins/leader-election";
import { RxDBMigrationPlugin } from "rxdb/plugins/migration-schema";
import { getRxStorageDexie } from "rxdb/plugins/storage-dexie";
import { Provider } from "rxdb-hooks";
import { useDeviceSecretQuery } from "./__generated__/provider.gql";
import {
  AMP_SCHEMA,
  type Amp,
  RXDB_AMP_COLLECTION_NAME,
} from "./collections/amp";
import {
  CLINIC_INSURANCE_SCHEMA,
  type ClinicInsurance,
  RXDB_CLINIC_INSURANCE_COLLECTION_NAME,
} from "./collections/clinic-insurance";
import {
  CLINICAL_PRODUCT_CATEGORY_SCHEMA,
  type ClinicalProductCategory,
  RXDB_CLINICAL_PRODUCT_CATEGORY_COLLECTION_NAME,
} from "./collections/clinical-product-category";
import {
  CLINICAL_PRODUCT_FORM_SCHEMA,
  type ClinicalProductForm,
  RXDB_CLINICAL_PRODUCT_FORM_COLLECTION_NAME,
} from "./collections/clinical-product-form";
import {
  CONSULTATION_SCHEMA,
  type Consultation,
  RXDB_CONSULTATION_COLLECTION_NAME,
} from "./collections/consultation";
import {
  DISEASE_SCHEMA,
  type Disease,
  RXDB_DISEASE_COLLECTION_NAME,
} from "./collections/disease";
import {
  DISPENSATION_SCHEMA,
  type Dispensation,
  RXDB_DISPENSATION_COLLECTION_NAME,
} from "./collections/dispensation";
import {
  EPOCUS_BILLING_SCHEMA,
  type EpocusBilling,
  RXDB_EPOCUS_BILLING_COLLECTION_NAME,
} from "./collections/epocus-billing";
import {
  EPOCUS_CONSENT_DOCUMENT_SCHEMA,
  type EpocusConsentDocument,
  RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME,
} from "./collections/epocus-consent-document";
import {
  EPOCUS_ENCOUNTER_SCHEMA,
  type EpocusEncounter,
  RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME,
} from "./collections/epocus-encounter";
import {
  EPOCUS_ENCOUNTER_PATIENT_SCHEMA,
  type EpocusEncounterPatient,
  RXDB_EPOCUS_ENCOUNTER_PATIENT_COLLECTION_NAME,
} from "./collections/epocus-encounter-patient";
import {
  EPOCUS_EXAM_SCHEMA,
  type EpocusExam,
  RXDB_EPOCUS_EXAM_COLLECTION_NAME,
} from "./collections/epocus-exam";
import {
  EPOCUS_PATIENT_CONSENT_SCHEMA,
  type EpocusPatientConsent,
  RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME,
} from "./collections/epocus-patient-consent";
import {
  EPOCUS_PROBE_ASSOCIATION_SCHEMA,
  type EpocusProbeAssociation,
  RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME,
} from "./collections/epocus-probe-association";
import {
  EPOCUS_PROGRAM_SCHEMA,
  type EpocusProgram,
  RXDB_EPOCUS_PROGRAM_COLLECTION_NAME,
} from "./collections/epocus-program";
import {
  EPOCUS_REFERRAL_SCHEMA,
  type EpocusReferral,
  RXDB_EPOCUS_REFERRAL_COLLECTION_NAME,
} from "./collections/epocus-referral";
import {
  EPOCUS_REFERRAL_OUTCOME_SCHEMA,
  type EpocusReferralOutcome,
  RXDB_EPOCUS_REFERRAL_OUTCOME_COLLECTION_NAME,
} from "./collections/epocus-referral-outcome";
import {
  EPOCUS_REVIEW_SCHEMA,
  type EpocusReview,
  RXDB_EPOCUS_REVIEW_COLLECTION_NAME,
} from "./collections/epocus-review";
import {
  EXCHANGE_RATE_SCHEMA,
  type ExchangeRate,
  RXDB_EXCHANGE_RATE_COLLECTION_NAME,
} from "./collections/exchange-rate";
import {
  FACILITY_SCHEMA,
  type Facility,
  RXDB_FACILITY_COLLECTION_NAME,
} from "./collections/facility";
import {
  IMAGING_INSTANCE_SCHEMA,
  type ImagingInstance,
  RXDB_IMAGING_INSTANCE_COLLECTION_NAME,
} from "./collections/imaging-instance";
import {
  IMAGING_STUDY_SCHEMA,
  type ImagingStudy,
  RXDB_IMAGING_STUDY_COLLECTION_NAME,
} from "./collections/imaging-study";
import {
  INVESTIGATION_ACTIVITY_SCHEMA,
  type InvestigationActivity,
  RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME,
} from "./collections/investigation-activity";
import {
  INVESTIGATION_CATALOGUE_ITEM_SCHEMA,
  type InvestigationCatalogueItem,
  RXDB_INVESTIGATION_CATALOGUE_ITEM_COLLECTION_NAME,
} from "./collections/investigation-catalogue-item";
import {
  INVOICE_SCHEMA,
  type Invoice,
  RXDB_INVOICE_COLLECTION_NAME,
} from "./collections/invoice";
import {
  INVOICE_CONFLICT_CASE_SCHEMA,
  type InvoiceConflictCase,
  RXDB_INVOICE_CONFLICT_CASE_COLLECTION_NAME,
} from "./collections/invoice-conflict-case";
import {
  NON_PHARMA_ITEM_SCHEMA,
  type NonPharmaItem,
  RXDB_NON_PHARMA_ITEM_COLLECTION_NAME,
} from "./collections/non-pharma-item";
import {
  NOTIFICATION_SCHEMA,
  type Notification,
  RXDB_NOTIFICATION_COLLECTION_NAME,
} from "./collections/notification";
import {
  NOTIFICATION_BELL_STATE_SCHEMA,
  type NotificationBellState,
  RXDB_NOTIFICATION_BELL_STATE_COLLECTION_NAME,
} from "./collections/notification-bell-state";
import {
  NOTIFICATION_DISMISSAL_SCHEMA,
  type NotificationDismissal,
  RXDB_NOTIFICATION_DISMISSAL_COLLECTION_NAME,
} from "./collections/notification-dismissal";
import {
  NOTIFICATION_PREFERENCE_SCHEMA,
  type NotificationPreference,
  RXDB_NOTIFICATION_PREFERENCE_COLLECTION_NAME,
} from "./collections/notification-preference";
import {
  PATIENT_SCHEMA,
  type Patient,
  RXDB_PATIENT_COLLECTION_NAME,
} from "./collections/patient";
import {
  PRESCRIPTION_SCHEMA,
  type Prescription,
  RXDB_PRESCRIPTION_COLLECTION_NAME,
} from "./collections/prescription";
import {
  PRICE_SCHEMA,
  type Price,
  RXDB_PRICE_COLLECTION_NAME,
} from "./collections/price";
import {
  PRICE_ACKNOWLEDGEMENT_SCHEMA,
  type PriceAcknowledgement,
  RXDB_PRICE_ACKNOWLEDGEMENT_COLLECTION_NAME,
} from "./collections/price-acknowledgement";
import {
  PRICE_LIST_FACILITY_SCHEMA,
  type PriceListFacility,
  RXDB_PRICE_LIST_FACILITY_COLLECTION_NAME,
} from "./collections/price-list-facility";
import {
  PRICE_LIST_ITEM_SCHEMA,
  type PriceListItem,
  RXDB_PRICE_LIST_ITEM_COLLECTION_NAME,
} from "./collections/price-list-item";
import {
  PRICE_LIST_ITEM_EXTERNAL_REFERENCE_SCHEMA,
  type PriceListItemExternalReference,
  RXDB_PRICE_LIST_ITEM_EXTERNAL_REFERENCE_COLLECTION_NAME,
} from "./collections/price-list-item-external-reference";
import {
  PRICE_LIST_ITEM_NAME_SCHEMA,
  type PriceListItemName,
  RXDB_PRICE_LIST_ITEM_NAME_COLLECTION_NAME,
} from "./collections/price-list-item-name";
import {
  PRICE_LIST_REVENUE_DEPARTMENT_SCHEMA,
  type PriceListRevenueDepartment,
  RXDB_PRICE_LIST_REVENUE_DEPARTMENT_COLLECTION_NAME,
} from "./collections/price-list-revenue-department";
import {
  PRICE_LIST_TARIFF_SCHEMA,
  type PriceListTariff,
  RXDB_PRICE_LIST_TARIFF_COLLECTION_NAME,
} from "./collections/price-list-tariff";
import {
  REFERRAL_SCHEMA,
  type Referral,
  RXDB_REFERRAL_COLLECTION_NAME,
} from "./collections/referral";
import {
  REFERRAL_FEEDBACK_SCHEMA,
  type ReferralFeedback,
  RXDB_REFERRAL_FEEDBACK_COLLECTION_NAME,
} from "./collections/referral-feedback";
import {
  RXDB_SYMPTOM_COLLECTION_NAME,
  SYMPTOM_SCHEMA,
  type Symptom,
} from "./collections/symptom";
import {
  RXDB_SYNC_CHECKPOINT_COLLECTION_NAME,
  SYNC_CHECKPOINT_SCHEMA,
  type SyncCheckpoint,
} from "./collections/sync-checkpoint";
import {
  RXDB_VMP_COLLECTION_NAME,
  VMP_SCHEMA,
  type Vmp,
} from "./collections/vmp";
import {
  RXDB_VTM_COLLECTION_NAME,
  VTM_SCHEMA,
  type Vtm,
} from "./collections/vtm";
import {
  invoiceConflictHandler,
  registerInvoiceConflictCaseCollection,
} from "./conflict-handlers/invoice";
import { useRxdbReplication } from "./hooks/use-rxdb-replication";
import { useTrackRxdbPatients } from "./hooks/use-track-rxdb-patients";
import { AMP_MIGRATION_STRATEGIES } from "./migration-strategies/amp";
import { CLINIC_INSURANCE_MIGRATION_STRATEGIES } from "./migration-strategies/clinic-insurance";
import { CLINICAL_PRODUCT_CATEGORY_MIGRATION_STRATEGIES } from "./migration-strategies/clinical-product-category";
import { CLINICAL_PRODUCT_FORM_MIGRATION_STRATEGIES } from "./migration-strategies/clinical-product-form";
import { CONSULTATION_MIGRATION_STRATEGIES } from "./migration-strategies/consultation";
import { DISEASE_MIGRATION_STRATEGIES } from "./migration-strategies/disease";
import { DISPENSATION_MIGRATION_STRATEGIES } from "./migration-strategies/dispensation";
import { EPOCUS_BILLING_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-billing";
import { EPOCUS_CONSENT_DOCUMENT_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-consent-document";
import { EPOCUS_ENCOUNTER_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-encounter";
import { EPOCUS_ENCOUNTER_PATIENT_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-encounter-patient";
import { EPOCUS_EXAM_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-exam";
import { EPOCUS_PATIENT_CONSENT_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-patient-consent";
import { EPOCUS_PROBE_ASSOCIATION_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-probe-association";
import { EPOCUS_PROGRAM_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-program";
import { EPOCUS_REFERRAL_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-referral";
import { EPOCUS_REFERRAL_OUTCOME_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-referral-outcome";
import { EPOCUS_REVIEW_MIGRATION_STRATEGIES } from "./migration-strategies/epocus-review";
import { EXCHANGE_RATE_MIGRATION_STRATEGIES } from "./migration-strategies/exchange-rate";
import { FACILITY_MIGRATION_STRATEGIES } from "./migration-strategies/facility";
import { IMAGING_INSTANCE_MIGRATION_STRATEGIES } from "./migration-strategies/imaging-instance";
import { IMAGING_STUDY_MIGRATION_STRATEGIES } from "./migration-strategies/imaging-study";
import { INVESTIGATION_ACTIVITY_MIGRATION_STRATEGIES } from "./migration-strategies/investigation-activity";
import { INVESTIGATION_CATALOGUE_ITEM_MIGRATION_STRATEGIES } from "./migration-strategies/investigation-catalogue-item";
import { INVOICE_MIGRATION_STRATEGIES } from "./migration-strategies/invoice";
import { INVOICE_CONFLICT_CASE_MIGRATION_STRATEGIES } from "./migration-strategies/invoice-conflict-case";
import { NON_PHARMA_ITEM_MIGRATION_STRATEGIES } from "./migration-strategies/non-pharma-item";
import { NOTIFICATION_MIGRATION_STRATEGIES } from "./migration-strategies/notification";
import { NOTIFICATION_BELL_STATE_MIGRATION_STRATEGIES } from "./migration-strategies/notification-bell-state";
import { NOTIFICATION_DISMISSAL_MIGRATION_STRATEGIES } from "./migration-strategies/notification-dismissal";
import { NOTIFICATION_PREFERENCE_MIGRATION_STRATEGIES } from "./migration-strategies/notification-preference";
import { PATIENT_MIGRATION_STRATEGIES } from "./migration-strategies/patient";
import { PRESCRIPTION_MIGRATION_STRATEGIES } from "./migration-strategies/prescription";
import { PRICE_MIGRATION_STRATEGIES } from "./migration-strategies/price";
import { PRICE_ACKNOWLEDGEMENT_MIGRATION_STRATEGIES } from "./migration-strategies/price-acknowledgement";
import { PRICE_LIST_FACILITY_MIGRATION_STRATEGIES } from "./migration-strategies/price-list-facility";
import { PRICE_LIST_ITEM_MIGRATION_STRATEGIES } from "./migration-strategies/price-list-item";
import { PRICE_LIST_ITEM_EXTERNAL_REFERENCE_MIGRATION_STRATEGIES } from "./migration-strategies/price-list-item-external-reference";
import { PRICE_LIST_ITEM_NAME_MIGRATION_STRATEGIES } from "./migration-strategies/price-list-item-name";
import { PRICE_LIST_REVENUE_DEPARTMENT_MIGRATION_STRATEGIES } from "./migration-strategies/price-list-revenue-department";
import { PRICE_LIST_TARIFF_MIGRATION_STRATEGIES } from "./migration-strategies/price-list-tariff";
import { REFERRAL_MIGRATION_STRATEGIES } from "./migration-strategies/referral";
import { REFERRAL_FEEDBACK_MIGRATION_STRATEGIES } from "./migration-strategies/referral-feedback";
import { SYMPTOM_MIGRATION_STRATEGIES } from "./migration-strategies/symptom";
import { SYNC_CHECKPOINT_MIGRATION_STRATEGIES } from "./migration-strategies/sync-checkpoint";
import { VMP_MIGRATION_STRATEGIES } from "./migration-strategies/vmp";
import { VTM_MIGRATION_STRATEGIES } from "./migration-strategies/vtm";
import type { CollectionReplicationState } from "./replication/use-gql-replication";
export const DATABASE_NAME = "elephant";
export interface RxDBCollections {
  [RXDB_PATIENT_COLLECTION_NAME]: RxCollection<Patient>;
  [RXDB_EPOCUS_BILLING_COLLECTION_NAME]: RxCollection<EpocusBilling>;
  [RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME]: RxCollection<EpocusConsentDocument>;
  [RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME]: RxCollection<EpocusEncounter>;
  [RXDB_EPOCUS_ENCOUNTER_PATIENT_COLLECTION_NAME]: RxCollection<EpocusEncounterPatient>;
  [RXDB_EPOCUS_EXAM_COLLECTION_NAME]: RxCollection<EpocusExam>;
  [RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME]: RxCollection<EpocusPatientConsent>;
  [RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME]: RxCollection<EpocusProbeAssociation>;
  [RXDB_EPOCUS_PROGRAM_COLLECTION_NAME]: RxCollection<EpocusProgram>;
  [RXDB_EPOCUS_REFERRAL_COLLECTION_NAME]: RxCollection<EpocusReferral>;
  [RXDB_EPOCUS_REFERRAL_OUTCOME_COLLECTION_NAME]: RxCollection<EpocusReferralOutcome>;
  [RXDB_EPOCUS_REVIEW_COLLECTION_NAME]: RxCollection<EpocusReview>;

  [RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME]: RxCollection<InvestigationActivity>;

  [RXDB_INVESTIGATION_CATALOGUE_ITEM_COLLECTION_NAME]: RxCollection<InvestigationCatalogueItem>;
  [RXDB_IMAGING_STUDY_COLLECTION_NAME]: RxCollection<ImagingStudy>;
  [RXDB_IMAGING_INSTANCE_COLLECTION_NAME]: RxCollection<ImagingInstance>;
  [RXDB_VTM_COLLECTION_NAME]: RxCollection<Vtm>;
  [RXDB_VMP_COLLECTION_NAME]: RxCollection<Vmp>;
  [RXDB_AMP_COLLECTION_NAME]: RxCollection<Amp>;
  [RXDB_NON_PHARMA_ITEM_COLLECTION_NAME]: RxCollection<NonPharmaItem>;
  [RXDB_CLINICAL_PRODUCT_FORM_COLLECTION_NAME]: RxCollection<ClinicalProductForm>;
  [RXDB_PRESCRIPTION_COLLECTION_NAME]: RxCollection<Prescription>;
  [RXDB_CLINICAL_PRODUCT_CATEGORY_COLLECTION_NAME]: RxCollection<ClinicalProductCategory>;
  [RXDB_DISPENSATION_COLLECTION_NAME]: RxCollection<Dispensation>;
  [RXDB_CONSULTATION_COLLECTION_NAME]: RxCollection<Consultation>;
  [RXDB_REFERRAL_COLLECTION_NAME]: RxCollection<Referral>;
  [RXDB_REFERRAL_FEEDBACK_COLLECTION_NAME]: RxCollection<ReferralFeedback>;
  [RXDB_SYNC_CHECKPOINT_COLLECTION_NAME]: RxCollection<SyncCheckpoint>;
  [RXDB_DISEASE_COLLECTION_NAME]: RxCollection<Disease>;
  [RXDB_SYMPTOM_COLLECTION_NAME]: RxCollection<Symptom>;
  [RXDB_FACILITY_COLLECTION_NAME]: RxCollection<Facility>;
  [RXDB_PRICE_LIST_FACILITY_COLLECTION_NAME]: RxCollection<PriceListFacility>;
  [RXDB_PRICE_COLLECTION_NAME]: RxCollection<Price>;
  [RXDB_PRICE_LIST_TARIFF_COLLECTION_NAME]: RxCollection<PriceListTariff>;
  [RXDB_PRICE_LIST_REVENUE_DEPARTMENT_COLLECTION_NAME]: RxCollection<PriceListRevenueDepartment>;
  [RXDB_PRICE_LIST_ITEM_COLLECTION_NAME]: RxCollection<PriceListItem>;
  [RXDB_PRICE_LIST_ITEM_NAME_COLLECTION_NAME]: RxCollection<PriceListItemName>;
  [RXDB_PRICE_LIST_ITEM_EXTERNAL_REFERENCE_COLLECTION_NAME]: RxCollection<PriceListItemExternalReference>;
  [RXDB_INVOICE_COLLECTION_NAME]: RxCollection<Invoice>;
  [RXDB_INVOICE_CONFLICT_CASE_COLLECTION_NAME]: RxCollection<InvoiceConflictCase>;
  [RXDB_CLINIC_INSURANCE_COLLECTION_NAME]: RxCollection<ClinicInsurance>;
  [RXDB_EXCHANGE_RATE_COLLECTION_NAME]: RxCollection<ExchangeRate>;
  [RXDB_PRICE_ACKNOWLEDGEMENT_COLLECTION_NAME]: RxCollection<PriceAcknowledgement>;
  [RXDB_NOTIFICATION_COLLECTION_NAME]: RxCollection<Notification>;
  [RXDB_NOTIFICATION_BELL_STATE_COLLECTION_NAME]: RxCollection<NotificationBellState>;
  [RXDB_NOTIFICATION_DISMISSAL_COLLECTION_NAME]: RxCollection<NotificationDismissal>;
  [RXDB_NOTIFICATION_PREFERENCE_COLLECTION_NAME]: RxCollection<NotificationPreference>;
}

export const RXDB_COLLECTIONS = {
  [RXDB_PATIENT_COLLECTION_NAME]: {
    schema: PATIENT_SCHEMA,
    migrationStrategies: PATIENT_MIGRATION_STRATEGIES,
  },
  [RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME]: {
    schema: INVESTIGATION_ACTIVITY_SCHEMA,
    migrationStrategies: INVESTIGATION_ACTIVITY_MIGRATION_STRATEGIES,
  },
  [RXDB_INVESTIGATION_CATALOGUE_ITEM_COLLECTION_NAME]: {
    schema: INVESTIGATION_CATALOGUE_ITEM_SCHEMA,
    migrationStrategies: INVESTIGATION_CATALOGUE_ITEM_MIGRATION_STRATEGIES,
  },
  [RXDB_VTM_COLLECTION_NAME]: {
    schema: VTM_SCHEMA,
    migrationStrategies: VTM_MIGRATION_STRATEGIES,
  },
  [RXDB_VMP_COLLECTION_NAME]: {
    schema: VMP_SCHEMA,
    migrationStrategies: VMP_MIGRATION_STRATEGIES,
  },
  [RXDB_AMP_COLLECTION_NAME]: {
    schema: AMP_SCHEMA,
    migrationStrategies: AMP_MIGRATION_STRATEGIES,
  },
  [RXDB_NON_PHARMA_ITEM_COLLECTION_NAME]: {
    schema: NON_PHARMA_ITEM_SCHEMA,
    migrationStrategies: NON_PHARMA_ITEM_MIGRATION_STRATEGIES,
  },
  [RXDB_CLINICAL_PRODUCT_FORM_COLLECTION_NAME]: {
    schema: CLINICAL_PRODUCT_FORM_SCHEMA,
    migrationStrategies: CLINICAL_PRODUCT_FORM_MIGRATION_STRATEGIES,
  },
  [RXDB_PRESCRIPTION_COLLECTION_NAME]: {
    schema: PRESCRIPTION_SCHEMA,
    migrationStrategies: PRESCRIPTION_MIGRATION_STRATEGIES,
  },
  [RXDB_CLINICAL_PRODUCT_CATEGORY_COLLECTION_NAME]: {
    schema: CLINICAL_PRODUCT_CATEGORY_SCHEMA,
    migrationStrategies: CLINICAL_PRODUCT_CATEGORY_MIGRATION_STRATEGIES,
  },
  [RXDB_DISPENSATION_COLLECTION_NAME]: {
    schema: DISPENSATION_SCHEMA,
    migrationStrategies: DISPENSATION_MIGRATION_STRATEGIES,
  },
  [RXDB_REFERRAL_COLLECTION_NAME]: {
    schema: REFERRAL_SCHEMA,
    migrationStrategies: REFERRAL_MIGRATION_STRATEGIES,
  },
  [RXDB_REFERRAL_FEEDBACK_COLLECTION_NAME]: {
    schema: REFERRAL_FEEDBACK_SCHEMA,
    migrationStrategies: REFERRAL_FEEDBACK_MIGRATION_STRATEGIES,
  },
  [RXDB_DISEASE_COLLECTION_NAME]: {
    schema: DISEASE_SCHEMA,
    migrationStrategies: DISEASE_MIGRATION_STRATEGIES,
  },
  [RXDB_CONSULTATION_COLLECTION_NAME]: {
    schema: CONSULTATION_SCHEMA,
    migrationStrategies: CONSULTATION_MIGRATION_STRATEGIES,
  },
  [RXDB_SYNC_CHECKPOINT_COLLECTION_NAME]: {
    schema: SYNC_CHECKPOINT_SCHEMA,
    migrationStrategies: SYNC_CHECKPOINT_MIGRATION_STRATEGIES,
  },
  [RXDB_SYMPTOM_COLLECTION_NAME]: {
    schema: SYMPTOM_SCHEMA,
    migrationStrategies: SYMPTOM_MIGRATION_STRATEGIES,
  },
  [RXDB_FACILITY_COLLECTION_NAME]: {
    schema: FACILITY_SCHEMA,
    migrationStrategies: FACILITY_MIGRATION_STRATEGIES,
  },
  [RXDB_IMAGING_STUDY_COLLECTION_NAME]: {
    schema: IMAGING_STUDY_SCHEMA,
    migrationStrategies: IMAGING_STUDY_MIGRATION_STRATEGIES,
  },
  [RXDB_IMAGING_INSTANCE_COLLECTION_NAME]: {
    schema: IMAGING_INSTANCE_SCHEMA,
    migrationStrategies: IMAGING_INSTANCE_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_BILLING_COLLECTION_NAME]: {
    schema: EPOCUS_BILLING_SCHEMA,
    migrationStrategies: EPOCUS_BILLING_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_CONSENT_DOCUMENT_COLLECTION_NAME]: {
    schema: EPOCUS_CONSENT_DOCUMENT_SCHEMA,
    migrationStrategies: EPOCUS_CONSENT_DOCUMENT_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_ENCOUNTER_COLLECTION_NAME]: {
    schema: EPOCUS_ENCOUNTER_SCHEMA,
    migrationStrategies: EPOCUS_ENCOUNTER_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_ENCOUNTER_PATIENT_COLLECTION_NAME]: {
    schema: EPOCUS_ENCOUNTER_PATIENT_SCHEMA,
    migrationStrategies: EPOCUS_ENCOUNTER_PATIENT_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_EXAM_COLLECTION_NAME]: {
    schema: EPOCUS_EXAM_SCHEMA,
    migrationStrategies: EPOCUS_EXAM_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_PATIENT_CONSENT_COLLECTION_NAME]: {
    schema: EPOCUS_PATIENT_CONSENT_SCHEMA,
    migrationStrategies: EPOCUS_PATIENT_CONSENT_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_PROBE_ASSOCIATION_COLLECTION_NAME]: {
    schema: EPOCUS_PROBE_ASSOCIATION_SCHEMA,
    migrationStrategies: EPOCUS_PROBE_ASSOCIATION_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_PROGRAM_COLLECTION_NAME]: {
    schema: EPOCUS_PROGRAM_SCHEMA,
    migrationStrategies: EPOCUS_PROGRAM_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_REFERRAL_COLLECTION_NAME]: {
    schema: EPOCUS_REFERRAL_SCHEMA,
    migrationStrategies: EPOCUS_REFERRAL_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_REFERRAL_OUTCOME_COLLECTION_NAME]: {
    schema: EPOCUS_REFERRAL_OUTCOME_SCHEMA,
    migrationStrategies: EPOCUS_REFERRAL_OUTCOME_MIGRATION_STRATEGIES,
  },
  [RXDB_EPOCUS_REVIEW_COLLECTION_NAME]: {
    schema: EPOCUS_REVIEW_SCHEMA,
    migrationStrategies: EPOCUS_REVIEW_MIGRATION_STRATEGIES,
  },
  [RXDB_PRICE_LIST_FACILITY_COLLECTION_NAME]: {
    schema: PRICE_LIST_FACILITY_SCHEMA,
    migrationStrategies: PRICE_LIST_FACILITY_MIGRATION_STRATEGIES,
  },
  [RXDB_PRICE_COLLECTION_NAME]: {
    schema: PRICE_SCHEMA,
    migrationStrategies: PRICE_MIGRATION_STRATEGIES,
  },
  [RXDB_PRICE_LIST_TARIFF_COLLECTION_NAME]: {
    schema: PRICE_LIST_TARIFF_SCHEMA,
    migrationStrategies: PRICE_LIST_TARIFF_MIGRATION_STRATEGIES,
  },
  [RXDB_PRICE_LIST_REVENUE_DEPARTMENT_COLLECTION_NAME]: {
    schema: PRICE_LIST_REVENUE_DEPARTMENT_SCHEMA,
    migrationStrategies: PRICE_LIST_REVENUE_DEPARTMENT_MIGRATION_STRATEGIES,
  },
  [RXDB_PRICE_LIST_ITEM_COLLECTION_NAME]: {
    schema: PRICE_LIST_ITEM_SCHEMA,
    migrationStrategies: PRICE_LIST_ITEM_MIGRATION_STRATEGIES,
  },
  [RXDB_PRICE_LIST_ITEM_NAME_COLLECTION_NAME]: {
    schema: PRICE_LIST_ITEM_NAME_SCHEMA,
    migrationStrategies: PRICE_LIST_ITEM_NAME_MIGRATION_STRATEGIES,
  },
  [RXDB_PRICE_LIST_ITEM_EXTERNAL_REFERENCE_COLLECTION_NAME]: {
    schema: PRICE_LIST_ITEM_EXTERNAL_REFERENCE_SCHEMA,
    migrationStrategies:
      PRICE_LIST_ITEM_EXTERNAL_REFERENCE_MIGRATION_STRATEGIES,
  },
  [RXDB_INVOICE_COLLECTION_NAME]: {
    schema: INVOICE_SCHEMA,
    migrationStrategies: INVOICE_MIGRATION_STRATEGIES,
    conflictHandler: invoiceConflictHandler,
  },
  [RXDB_INVOICE_CONFLICT_CASE_COLLECTION_NAME]: {
    schema: INVOICE_CONFLICT_CASE_SCHEMA,
    migrationStrategies: INVOICE_CONFLICT_CASE_MIGRATION_STRATEGIES,
  },
  [RXDB_PRICE_ACKNOWLEDGEMENT_COLLECTION_NAME]: {
    schema: PRICE_ACKNOWLEDGEMENT_SCHEMA,
    migrationStrategies: PRICE_ACKNOWLEDGEMENT_MIGRATION_STRATEGIES,
  },
  [RXDB_NOTIFICATION_COLLECTION_NAME]: {
    schema: NOTIFICATION_SCHEMA,
    migrationStrategies: NOTIFICATION_MIGRATION_STRATEGIES,
  },
  [RXDB_NOTIFICATION_BELL_STATE_COLLECTION_NAME]: {
    schema: NOTIFICATION_BELL_STATE_SCHEMA,
    migrationStrategies: NOTIFICATION_BELL_STATE_MIGRATION_STRATEGIES,
  },
  [RXDB_NOTIFICATION_DISMISSAL_COLLECTION_NAME]: {
    schema: NOTIFICATION_DISMISSAL_SCHEMA,
    migrationStrategies: NOTIFICATION_DISMISSAL_MIGRATION_STRATEGIES,
  },
  [RXDB_NOTIFICATION_PREFERENCE_COLLECTION_NAME]: {
    schema: NOTIFICATION_PREFERENCE_SCHEMA,
    migrationStrategies: NOTIFICATION_PREFERENCE_MIGRATION_STRATEGIES,
  },
  [RXDB_CLINIC_INSURANCE_COLLECTION_NAME]: {
    schema: CLINIC_INSURANCE_SCHEMA,
    migrationStrategies: CLINIC_INSURANCE_MIGRATION_STRATEGIES,
  },
  [RXDB_EXCHANGE_RATE_COLLECTION_NAME]: {
    schema: EXCHANGE_RATE_SCHEMA,
    migrationStrategies: EXCHANGE_RATE_MIGRATION_STRATEGIES,
  },
} satisfies Parameters<RxDatabase["addCollections"]>[0];

type RxCollectionName =
  | "amp"
  | "nonPharmaItem"
  | "clinicalProductCategory"
  | "clinicalProductForm"
  | "consultation"
  | "disease"
  | "dispensation"
  | "epocus-billing"
  | "epocusConsentDocument"
  | "epocusEncounter"
  | "epocusExam"
  | "epocusPatientConsent"
  | "epocusProbeAssociation"
  | "epocus-program"
  | "epocus-referral"
  | "epocus-referral-outcome"
  | "epocusReview"
  | "facility"
  | "imagingStudy"
  | "imagingInstance"
  | "investigationActivity"
  | "investigationCatalogueItem"
  | "patient"
  | "prescription"
  | "referral"
  | "referral-feedback"
  | "vmp"
  | "vtm"
  | "symptom"
  | "priceListFacility"
  | "price"
  | "priceListTariff"
  | "priceListRevenueDepartment"
  | "priceListItem"
  | "priceListItemName"
  | "priceListItemExternalReference"
  | "invoice"
  | "priceAcknowledgement"
  | "notification"
  | "notificationBellState"
  | "notificationDismissal"
  | "notificationPreference"
  | "clinic-insurance"
  | "exchangeRate";

export type ReplicationState = {
  [K in RxCollectionName]: CollectionReplicationState;
} & {
  hasUnsyncedChanges: boolean;
};

// biome-ignore lint/suspicious/noExplicitAny: same type as lib
type Storage = RxStorage<any, any>;

// Plugins
addRxPlugin(RxDBMigrationPlugin);

/**
 * This plugin is not required by us, but is internally required by
 * RxDB when destroying the database. If not added here, RxDB will throw an
 * error when destroying the database and generate unnecessary noise in
 * our FE observability systems.
 */
addRxPlugin(RxDBLeaderElectionPlugin);

addRxPlugin(RxDBCleanupPlugin);

const getRxStorage = async (devToolsEnabled: boolean) => {
  let rxStorage: Storage = getRxStorageDexie();

  if (RXDB_ENCRYPTION_ENABLED) {
    console.debug("RxDB: adding encryption");
    rxStorage = wrappedKeyEncryptionCryptoJsStorage({ storage: rxStorage });
  } else {
    console.warn("RxDB: encryption disabled — IndexedDB holds plaintext");
  }

  if (devToolsEnabled) {
    console.debug("RxDB: adding schema validation");
    const { disableWarnings, RxDBDevModePlugin } = await import(
      "rxdb/plugins/dev-mode"
    );
    const { wrappedValidateAjvStorage } = await import("./validation");

    disableWarnings();
    addRxPlugin(RxDBDevModePlugin);

    rxStorage = wrappedValidateAjvStorage({ storage: rxStorage });
  }

  return rxStorage;
};

export const withoutEncryptedFields = <
  T extends Parameters<RxDatabase["addCollections"]>[0],
>(
  collections: T,
): T => {
  const entries = Object.entries(collections) as [
    string,
    RxCollectionCreator,
  ][];
  return Object.fromEntries(
    entries.map(([name, creator]) => [
      name,
      { ...creator, schema: { ...creator.schema, encrypted: [] } },
    ]),
  ) as T;
};

export enum RxDBInitialisationError {
  RECOVERABLE = "RECOVERABLE",
  FATAL = "FATAL",
}
export const RxDBContext = createContext<{
  replicationState: ReplicationState;
  db?: RxDatabase<RxDBCollections>;
  initialisationError?: RxDBInitialisationError;
} | null>(null);

export const RxDBProvider: FCwC<{
  dbOverride?: RxDatabase<RxDBCollections>;
}> = ({ children, dbOverride }) => {
  const { isLeader } = useTabLeadership();
  const user = useLazyUser();
  const { data: deviceSecretData, loading: deviceSecretLoading } =
    useDeviceSecretQuery({
      variables: {
        input: {
          lookupKey: user?.facilityId || "",
          lookupKeyOrigin: LookupKeyOrigin.FacilityId,
          purpose: DeviceSecretPurpose.RxdbEncryption,
        },
      },
      fetchPolicy: "network-only",
    });
  const deviceSecret = deviceSecretData?.deviceSecret;
  const [db, setDb] = useState<RxDatabase<RxDBCollections> | undefined>(
    () => dbOverride || undefined,
  );
  const initialising = useRef(false);
  const [initialisationError, setInitialisationError] = useState<
    RxDBInitialisationError | undefined
  >(undefined);
  const replicationState = useRxdbReplication(
    db as RxDatabase<RxDBCollections>,
  );
  useTrackRxdbPatients(db as RxDatabase<RxDBCollections>);

  const initDatabase = useCallback(async (password: string) => {
    if (initialising.current) return;
    initialising.current = true;
    const getStorage = () => getRxStorage(isDevEnvironment());

    const createDb = async (storage: Storage) => {
      const database = await createRxDatabase<RxDBCollections>({
        name: DATABASE_NAME,
        storage,
        password: RXDB_ENCRYPTION_ENABLED ? password : undefined,
        multiInstance: false,
      });
      await database.addCollections<RxDBCollections>(
        RXDB_ENCRYPTION_ENABLED
          ? RXDB_COLLECTIONS
          : withoutEncryptedFields(RXDB_COLLECTIONS),
      );
      registerInvoiceConflictCaseCollection(
        database.collections[RXDB_INVOICE_CONFLICT_CASE_COLLECTION_NAME],
      );
      setDb(database);
    };
    let initialStorage: Awaited<ReturnType<typeof getStorage>> | null = null;
    try {
      initialStorage = await getStorage();
      await createDb(initialStorage);
    } catch (error) {
      console.error("Error clearing or creating database", error);
      captureException(error);
      await handleError(
        error,
        async () => {
          if (initialStorage) {
            await removeRxDatabase(
              DATABASE_NAME,
              initialStorage,
              RXDB_ENCRYPTION_ENABLED ? password : undefined,
            );
          }
          setDb(undefined);
        },
        createDb,
        getStorage,
        setInitialisationError,
      );
    } finally {
      initialising.current = false;
    }
  }, []);

  useEffect(() => {
    if (
      db ||
      !isLeader ||
      deviceSecretLoading ||
      !deviceSecret?.value ||
      initialisationError
    ) {
      return;
    }

    initDatabase(deviceSecret.value).catch((error) => {
      console.error("Error initializing database", error);
      captureException(error);
      initialising.current = false;
    });
  }, [
    db,
    isLeader,
    initDatabase,
    deviceSecretLoading,
    deviceSecret?.value,
    initialisationError,
  ]);

  const rxContextValue = useMemo(
    () => ({
      replicationState,
      db,
      initialisationError,
    }),
    [replicationState, db, initialisationError],
  );

  return (
    <RxDBContext.Provider value={rxContextValue}>
      <Provider db={db}>{children}</Provider>
    </RxDBContext.Provider>
  );
};

export const useRxDBContext = () => {
  const context = useContext(RxDBContext);
  if (!context) {
    throw new Error("useRxDBContext must be used within a RxDBContextProvider");
  }
  return context;
};

// The values of this enum are the error codes thrown by RxDB that we care about
const RxErrorKeys: Record<string, RxError["code"]> = {
  WRONG_PASSWORD: "DB1", // https://rxdb.info/errors.html#DB1
  DB_ALREADY_EXISTS: "DB8", // https://rxdb.info/errors.html#DB8
};

const handleError = async (
  error: unknown,
  removeRxDb: () => Promise<void>,
  recreateDb: (storage: Storage) => Promise<void>,
  getStorage: () => Promise<Storage>,
  setInitialisationError: (error: RxDBInitialisationError) => void,
) => {
  if (error instanceof RxError) {
    switch (error.code) {
      case RxErrorKeys.WRONG_PASSWORD: {
        setInitialisationError(RxDBInitialisationError.RECOVERABLE);
        await removeRxDb();
        const storage = await getStorage();
        await recreateDb(storage);
        break;
      }
      case RxErrorKeys.DB_ALREADY_EXISTS: {
        setInitialisationError(RxDBInitialisationError.RECOVERABLE);
        break;
      }
      default: {
        setInitialisationError(RxDBInitialisationError.FATAL);
        break;
      }
    }
  } else {
    setInitialisationError(RxDBInitialisationError.FATAL);
  }
};

gql`
  query DeviceSecret($input: DeviceSecretInput!) {
    deviceSecret(input: $input) {
      value
  }
}`;
